import classNames from 'classnames';
import * as React from 'react';
import { NavigationLink } from '../NavigationLink/NavigationLink';
import type { NavigationAnchor } from '../Utils/types';
import { Collapse } from '~/Partials/Collapse/Collapse';
import { List } from '~/Partials/List/List';
import type { Theme } from '~/Utils/Theme';
import styles from './NavigationList.module.css';

interface NavigationListProps {
    isOpen: boolean;
    sectionAnchors: NavigationAnchor[];
    onItemClick: () => void;
    ariaControlsId: string;
    label: string;
    theme: Theme;
    currentItem?: NavigationAnchor;
}

const navigationVisibleClassName = 'StudyPageNavigation--open';

export const NavigationList: React.FC<NavigationListProps> = props => {
    const { isOpen, onItemClick, currentItem, sectionAnchors, ariaControlsId, label, theme } = props;
    const { withBoxShadow, withRoundedCorners } = theme;

    const renderItem = (item: NavigationAnchor, index: number) => {
        const isSelected = item.id === currentItem?.id;

        return (
            <li key={`${item.id}-${index}`} className={styles.ListItem}>
                <NavigationLink {...item} isSelected={isSelected} onClick={onItemClick} />
            </li>
        );
    };

    const addBodyClassNameOnNavigationExpand = React.useCallback(() => {
        if (isOpen) {
            const { body } = document;
            body.classList.add(navigationVisibleClassName);
        }
    }, [isOpen]);

    const removeBodyClassNameAfterNavigationCollapse = () => {
        if (!isOpen) {
            const { body } = document;
            body.classList.remove(navigationVisibleClassName);
        }
    };

    React.useEffect(() => {
        addBodyClassNameOnNavigationExpand();
    }, [isOpen, addBodyClassNameOnNavigationExpand]);

    const labelId = 'sub-menu-label';

    return (
        <div
            className={classNames(
                styles.NavigationList,
                withRoundedCorners && styles['Variant--rounded'],
                withBoxShadow && styles['Variant--shadowed']
            )}
        >
            <Collapse
                className={styles.Collapse}
                isOpen={isOpen}
                aria-hidden={isOpen ? 'false' : 'true'}
                onRest={removeBodyClassNameAfterNavigationCollapse}
            >
                <nav aria-labelledby={labelId} id={ariaControlsId} className={styles.NavElement}>
                    <h3 id={labelId} className="visuallyHidden">
                        {label}
                    </h3>
                    <List className={styles.List} items={sectionAnchors} renderItem={renderItem} />
                </nav>
            </Collapse>
        </div>
    );
};
